import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Caseload, CareCoordinatorReferral, Homeless, Referral, User } from 'types';

import AddButton from '../components/AddButton';
import Card from '../components/Card';
import ReferralCard from 'components/referrals/components/ReferralCard';
import { ReferralFactory } from 'components/referrals/factories/ReferralFactory';
import { UnifiedReferralModel } from 'components/referrals/models/UnifiedReferralModel';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    referralsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
    },
    referralList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
    },
    selector: {
        display: 'flex'
    },
    tab: {
        borderRadius: '16px',
        border: '1px solid #062D7A',
        color: '#062D7A',
        flex: 1,
        fontSize: '16px',
        textTransform: 'none',
        '&.Mui-selected': {
            backgroundColor: '#062D7A',
            color: '#FFFFFF'
        }
    }
}));

type Direction = 'outgoing' | 'incoming';

type ReferralsProps = {
    acceptReferral: (referralID: number) => void;
    caseloads: Caseload[];
    holder: Homeless;
    memberReferrals: Referral[];
    memberCareCoordinatorReferrals: CareCoordinatorReferral[];
    name: string;
    rejectReferral: (referralID: number) => void;
    removeMemberFromMyMembers: (caseloadID: number, rejected?: boolean) => void;
    saveMemberToMyMembers: (
        caseloadID: number,
        memberID: number,
        accepted?: boolean
    ) => void;
    setReferMemberPopUp: (value: boolean) => void;
    user: User;
};

const Referrals: React.FC<ReferralsProps> = ({
    acceptReferral,
    caseloads,
    holder,
    memberReferrals,
    memberCareCoordinatorReferrals,
    name,
    rejectReferral,
    removeMemberFromMyMembers,
    saveMemberToMyMembers,
    setReferMemberPopUp,
    user
}) => {
    const classes = useStyles();
    const isFirstRenderRef = useRef(true);
    const referralListRef = useRef<HTMLDivElement>(null);

    const { id: userID } = user;

    const matchHomelessToCaseload = useCallback((homeless_id: number) => {
        const caseload = caseloads.find(
            (caseload) => caseload.homeless_id === homeless_id
        );

        if (!caseload) {
            return null;
        }

        return caseload;
    }, [caseloads]);

    const myReferrals = memberReferrals?.filter(
        (referral) =>
            referral.referrer_id === userID || referral.referee_id === userID
    );

    const myCareCoordinatorReferrals = memberCareCoordinatorReferrals?.filter(
        (referral) =>
            referral.referrer_id === userID || referral.referee_id === userID
    );

    const [alignment, setAlignment] = useState('all');

    const determineDirection = useCallback((referral: Referral | CareCoordinatorReferral): Direction => {
        if (referral.referee?.id === userID && referral.referee?.organization_id) {
            return 'incoming';
        }
        return 'outgoing';
    }, [userID]);

    const referralsToRender = useMemo(() => {
        const allReferrals = alignment === 'all' 
            ? [...(memberReferrals || []), ...(memberCareCoordinatorReferrals || [])]
            : [...(myReferrals || []), ...(myCareCoordinatorReferrals || [])];
            
        return allReferrals
            .map(referral => {
                if ('is_active' in referral) {
                    return ReferralFactory.fromCareCoordinator(referral, {
                        profileStage: holder.profile_stage,
                        user
                    });
                }

                const caseload = matchHomelessToCaseload(referral.homeless_id);
                return ReferralFactory.fromCaseManagement(referral, {
                    profileStage: holder.profile_stage,
                    user,
                    direction: determineDirection(referral),
                    caseloadReferralIDs: caseload?.referral_ids || [],
                    caseloadUserID: caseload?.user_id
                });
            })
            .filter((referral): referral is UnifiedReferralModel => referral !== null)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [alignment, memberReferrals, myReferrals, memberCareCoordinatorReferrals, myCareCoordinatorReferrals, holder.profile_stage, user, matchHomelessToCaseload, determineDirection]);

    const handleChange = useCallback(
        (_event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
            setAlignment(newAlignment);
        },
        []
    );

    const openReferMemberPopUp = useCallback(() => {
        setReferMemberPopUp(true);
    }, [setReferMemberPopUp]);

    useEffect(() => {
        if (referralListRef.current && !isFirstRenderRef.current) {
            referralListRef.current.scrollIntoView({ behavior: 'smooth' });
        } else {
            isFirstRenderRef.current = false;
        }
    }, [alignment]);

    return (
        <div className={classes.referralsContainer} ref={referralListRef} data-testid="referrals-section">
            <Card info name={name} referrals />
            <ToggleButtonGroup
                className={classes.selector}
                aria-label="Referrals"
                exclusive
                onChange={handleChange}
                value={alignment}
            >
                <ToggleButton className={classes.tab} value="all">
                    All Referrals
                </ToggleButton>
                <ToggleButton className={classes.tab} value="my">
                    My Referrals
                    {(myReferrals?.length || 0) + (myCareCoordinatorReferrals?.length || 0) > 0 && 
                        ` (${(myReferrals?.length || 0) + (myCareCoordinatorReferrals?.length || 0)})`}
                </ToggleButton>
            </ToggleButtonGroup>
            {referralsToRender.length > 0 && (
                <div className={classes.referralList}>
                    {referralsToRender.map((referral) => {
                        // Only get caseload for regular referrals that need actions
                        const maybeCaseload = referral.type === 'case_management' && referral.shouldShowButtons
                            ? matchHomelessToCaseload(holder.id)
                            : undefined;
                            
                        const caseload = maybeCaseload || undefined;

                        return (
                            <ReferralCard
                                key={referral.id}
                                acceptReferral={acceptReferral}
                                caseload={caseload}
                                homeless={holder}
                                profile
                                referral={referral}
                                rejectReferral={rejectReferral}
                                removeMemberFromMyMembers={removeMemberFromMyMembers}
                                saveMemberToMyMembers={saveMemberToMyMembers}
                            />
                        );
                    })}
                </div>
            )}
            <AddButton action={openReferMemberPopUp} type="referral" />
        </div>
    );
};

export default Referrals;
